/**
 * MintSuccess — the studios' result panel. It REPLACES the mint form (the
 * form used to stay live under the result, one click away from minting the
 * same pass twice) and scrolls itself into view (it used to appear below the
 * fold). Offers the wallet QR/link, the pass page and a fresh form.
 */
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import { useT } from '../../i18n';
import type { DemoPass } from '../../types';
import { WalletSaveCard } from '../WalletSaveCard';

export interface MintSuccessProps {
  pass: DemoPass;
  /** Back to an empty form for the next pass of the same template. */
  onAnother: () => void;
}

export function MintSuccess({ pass, onAnother }: MintSuccessProps): JSX.Element {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // jsdom has no scrollIntoView — optional call keeps tests quiet.
    ref.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, []);
  return (
    <div
      ref={ref}
      data-testid="mint-success"
      className="rounded-md bg-emerald-50 ring-1 ring-emerald-200 p-5 space-y-4"
    >
      <div>
        <h2 className="font-semibold text-slate-900">{t('mint.done')}</h2>
        <p className="text-sm text-slate-600 mt-1">
          {t('mint.doneFor', { name: pass.recipientName })}
        </p>
      </div>
      <WalletSaveCard appleSaveUrl={pass.appleSaveUrl} googleSaveUrl={pass.googleSaveUrl} />
      <div className="flex flex-wrap items-center gap-3 pt-1">
        <Link to={`/passes/${pass.id}`} className="btn-primary">
          {t('mint.openPass')}
        </Link>
        <button type="button" onClick={onAnother} className="btn-secondary">
          {t('mint.another')}
        </button>
      </div>
    </div>
  );
}
